import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Chart } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState, useRef } from "react";
import ErrorResponse from "./ErrorResponse";
import { Card, Col, Stack } from "react-bootstrap";
import LoadingIndicator from "./LoadingIndicator";
import { DescriptionWithDownloadRow } from "../components/pageSections";
import DataDescription from "./DataDescription";
import RATooltip from "./RATooltip";
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup";
import ToggleButton from "react-bootstrap/ToggleButton";
import { useRouteLoaderData } from "react-router-dom";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

const colors = ["#DAD45B", "#CC9A6E", "#F0A6CA", "#77B77A", "#87CEEB", "#7BB1B1", "#66A3D2", "#4682B4"];

export default function ReleaseYearChart({ fullData, data, endYear, loading, error, submittedMarketName, moduleName, isCompetitor = false }) {
    const [grouping, setGrouping] = useState(false);
    const decadesBreakdownRef = useRef(null);
    const { radios, competitorRadios } = useRouteLoaderData("root");
    const [resolvedRadios, setResolvedRadios] = useState([]);
    const [csvData, setCsvData] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState(null);

    const handleGroupingChange = value => {
        setGrouping(value);
    };

    useEffect(() => {
        async function resolveRadios() {
            const radiosData = await radios;
            const competitorRadiosData = await competitorRadios;
            setResolvedRadios(radiosData.data.concat(competitorRadiosData.data));
        }
        if (radios && competitorRadios) resolveRadios();
    }, [radios, competitorRadios]);

    const getDynamicDecades = endYear => {
        const startDecades = [1960, 1970, 1980, 1990, 2000, 2010, 2020];
        const result = [];

        for (let i = 0; i < startDecades.length; i++) {
            const decadeStart = startDecades[i];
            const nextDecadeStart = startDecades[i + 1] || null;

            if (!nextDecadeStart && endYear >= decadeStart) {
                const cutoffYear = Math.min(endYear - 2, decadeStart + 9); // End of decade minus 2 years
                result.push({ label: `${decadeStart} - ${cutoffYear}`, range: [decadeStart, cutoffYear] });
                if (cutoffYear < endYear) {
                    result.push({ label: `${cutoffYear + 1}+`, range: [cutoffYear + 1, Infinity] });
                }
                break;
            }

            if (nextDecadeStart && endYear < nextDecadeStart) {
                const cutoffYear = Math.min(endYear - 2, nextDecadeStart - 1);
                result.push({ label: `${decadeStart} - ${cutoffYear}`, range: [decadeStart, cutoffYear] });
                if (cutoffYear < endYear) {
                    result.push({ label: `${cutoffYear + 1}+`, range: [cutoffYear + 1, Infinity] });
                }
                break;
            }

            result.push({ label: `${decadeStart}s`, range: [decadeStart, nextDecadeStart - 1] });
        }

        return result;
    };

    const dynamicDecades = useMemo(() => getDynamicDecades(endYear), [endYear]);

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return null;

        const songsWithYear = data.filter(song => song.release_year);
        const totalSongs = songsWithYear.length;

        if (isCompetitor) {
            const totalSpinsByRadio = songsWithYear.reduce((acc, song) => {
                song.radio_spins.forEach(({ radio_id, spins }) => {
                    acc[radio_id] = (acc[radio_id] || 0) + (spins || 0);
                });
                return acc;
            }, {});
            const totalSongsByRadio = songsWithYear.reduce((acc, song) => {
                song.radio_spins.forEach(({ radio_id, spins }) => {
                    if (spins > 0) {
                        acc[radio_id] = (acc[radio_id] || 0) + 1;
                    }
                });
                return acc;
            }, {});

            const decadeDataByRadio = dynamicDecades.map(({ label, range }) => {
                const songsInDecade = songsWithYear.filter(song => song.release_year >= range[0] && song.release_year <= range[1]);

                const songByRadio = songsInDecade.reduce((acc, song) => {
                    song.radio_spins.forEach(({ radio_id, spins }) => {
                        if (spins > 0) {
                            acc[radio_id] = (acc[radio_id] || 0) + 1;
                        }
                    });
                    return acc;
                }, {});

                const spinsByRadio = songsInDecade.reduce((acc, song) => {
                    song.radio_spins.forEach(({ radio_id, spins }) => {
                        acc[radio_id] = (acc[radio_id] || 0) + (spins || 0);
                    });
                    return acc;
                }, {});

                const radiosWithSpins = Object.entries(spinsByRadio).filter(([radio_id, spins]) => spins > 0);

                return {
                    decade: label,
                    songPercentageByRadio: radiosWithSpins.reduce((acc, [radio_id]) => {
                        const songCount = songByRadio[radio_id] || 0;
                        acc[radio_id] = totalSongsByRadio[radio_id] ? (songCount / totalSongsByRadio[radio_id]) * 100 : 0;
                        return acc;
                    }, {}),
                    spinsPercentageByRadio: radiosWithSpins.reduce((acc, [radio_id]) => {
                        const spinsCount = spinsByRadio[radio_id] || 0;
                        acc[radio_id] = totalSpinsByRadio[radio_id] ? (spinsCount / totalSpinsByRadio[radio_id]) * 100 : 0;
                        return acc;
                    }, {}),
                    songCountByRadio: radiosWithSpins.reduce((acc, [radio_id]) => {
                        const songCount = songByRadio[radio_id] || 0;
                        acc[radio_id] = songCount;
                        return acc;
                    }, {}),
                    spinsCountByRadio: radiosWithSpins.reduce((acc, [radio_id]) => {
                        const spinsCount = spinsByRadio[radio_id] || 0;
                        acc[radio_id] = spinsCount;
                        return acc;
                    }, {}),
                };
            });

            const filteredDecadeData = decadeDataByRadio.filter(d =>
                Object.values(grouping ? d.songPercentageByRadio : d.spinsPercentageByRadio).some(p => p >= 1),
            );
            const labels = filteredDecadeData.map(d => d.decade);

            const datasets = grouping
                ? Object.entries(totalSpinsByRadio).map(([radioId, totalSpins], index) => ({
                      label: `Radio ${resolvedRadios.find(x => x.radio_id == radioId)?.radio_name || "Unknown"} `,
                      data: filteredDecadeData.map(d => d.songPercentageByRadio[radioId] || 0),
                      backgroundColor: colors[index % colors.length],
                      borderColor: colors[index % colors.length],
                      borderWidth: 1,
                  }))
                : Object.entries(totalSpinsByRadio).map(([radioId, totalSpins], index) => ({
                      label: `Radio ${resolvedRadios.find(x => x.radio_id == radioId)?.radio_name || "Unknown"} `,
                      data: filteredDecadeData.map(d => d.spinsPercentageByRadio[radioId] || 0),
                      backgroundColor: colors[index % colors.length],
                      borderColor: colors[index % colors.length],
                      borderWidth: 1,
                  }));

            setCsvHeaders(["Decade", "Radio Name", "Release year (%)", grouping ? "Library" : "Rotations"]);

            const csvDataFixed = Object.entries(totalSpinsByRadio)
                .map(([radioId, totalSpins]) => {
                    return filteredDecadeData.map((decadeData, labelIndex) => {
                        const label = labels[labelIndex];
                        const radioName = resolvedRadios.find(x => x.radio_id == radioId)?.radio_name || "Unknown";
                        const value = grouping ? decadeData.songPercentageByRadio[radioId] || 0 : decadeData.spinsPercentageByRadio[radioId] || 0;
                        const rawCount = grouping ? decadeData.songCountByRadio[radioId] || 0 : decadeData.spinsCountByRadio[radioId] || 0;

                        return [label, radioName, value.toFixed(2), rawCount];
                    });
                })
                .flat()
                .filter(item => item.length > 0);

            setCsvData(csvDataFixed);

            return {
                labels,
                datasets,
            };
        } else {
            const totalSpins = songsWithYear.reduce((sum, song) => sum + (song.spins || 0), 0);

            const decadeData = dynamicDecades.map(({ label, range }) => {
                const songsInDecade = songsWithYear.filter(song => song.release_year >= range[0] && song.release_year <= range[1]);

                const songCount = songsInDecade.length;
                const spinsCount = songsInDecade.reduce((sum, song) => sum + (song.spins || 0), 0);

                return {
                    decade: label,
                    songCount,
                    spinsCount,
                    songPercentage: totalSongs > 0 ? (songCount / totalSongs) * 100 : 0,
                    spinsPercentage: totalSpins > 0 ? (spinsCount / totalSpins) * 100 : 0,
                };
            });

            const filteredDecadeData = decadeData.filter(d => (grouping ? d.songPercentage >= 1 : d.spinsPercentage >= 1));
            const labels = filteredDecadeData.map(d => d.decade);

            const dataSet = filteredDecadeData.map(d => {
                const percentage = grouping ? d.songPercentage : d.spinsPercentage;
                const rawCount = grouping ? d.songCount : d.spinsCount;
                return { percentage, rawCount };
            });

            const datasets = [
                {
                    label: grouping ? "Library (Percentage of Songs)" : "Rotations (Percentage of Spins)",
                    data: dataSet.map(d => d.percentage),
                    backgroundColor: colors,
                    borderColor: colors.slice(0, labels.length),
                    borderWidth: 1,
                },
            ];

            setCsvHeaders(["Decade", "Release year (%)", grouping ? "Library" : "Rotations"]);
            const csvData = datasets
                ? datasets
                      .map((dataset, index) => {
                          if (!Array.isArray(dataset.data)) {
                              console.warn(`Dataset ${index} has no valid 'data' array.`, dataset);
                              return [];
                          }
                          return dataset.data;
                      })
                      .flat()
                : [];

            const csvDataFixed = csvData.map((value, index) => {
                const rawCount = dataSet[index].rawCount;

                return [labels[index], value.toFixed(2), rawCount];
            });
            setCsvData(csvDataFixed);

            return {
                labels,
                datasets,
            };
        }
    }, [data, grouping, isCompetitor, dynamicDecades, resolvedRadios]);

    const chartOptions = useMemo(() => {
        const isSingleDataset = isCompetitor === 1;

        return {
            responsive: true,
            maintainAspectRatio: false,
            borderRadius: 5,
            plugins: {
                datalabels: {
                    color: "#333",
                    font: {
                        weight: "bold",
                        size: 14,
                    },
                    formatter: function (value) {
                        return value >= 1 ? `${value.toFixed(0)}` : "";
                    },
                },
                tooltip: {
                    enabled: !isSingleDataset,
                    filter: item => item.raw >= 1,
                    callbacks: {
                        label: function (item) {
                            return item.raw ? `${item.dataset.label}: ${item.raw.toFixed(0)}%` : "";
                        },
                    },
                },
                legend: {
                    display: false,
                    position: "top",
                    labels: {
                        boxWidth: 15,
                        padding: 10,
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    grid: {
                        display: false,
                    },
                    border: {
                        display: false,
                    },
                },
                x: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        font: {
                            size: 14,
                            weight: "bold",
                        },
                    },
                },
            },
            interaction: {
                mode: "index",
            },
        };
    }, [isCompetitor]);

    return (
        <div>
            {data && fullData && (
                <>
                    <div ref={decadesBreakdownRef}>
                        <DescriptionWithDownloadRow
                            screenshotContainer={decadesBreakdownRef}
                            filename={`Decade Breakdown for ${grouping ? "Library" : "Rotation"} for ${
                                submittedMarketName ? submittedMarketName : resolvedRadios.find(x => x.radio_id === fullData?.radio_id)?.radio_name || "Unknown"
                            }`}
                            data={csvData}
                            csvHeaders={csvHeaders}>
                            <h4 className="fw-bold text-uppercase mb-1 d-flex ">Decades Breakdown</h4>
                            <DataDescription>
                                {fullData?.market_id ? (
                                    <DataDescription.Simple label="Market" value={submittedMarketName} />
                                ) : (
                                    <DataDescription.Station radioId={fullData?.radio_id} />
                                )}
                                <DataDescription.DateRange startDate={fullData?.start_date} endDate={fullData?.end_date} />
                                <DataDescription.TimeRange startTime={fullData?.start_time} endTime={fullData?.end_time} />
                                <DataDescription.Perspective days={fullData?.days} />
                            </DataDescription>
                        </DescriptionWithDownloadRow>
                        <Stack direction="horizontal" gap={2}>
                            <span>
                                Breakdown
                                <RATooltip>
                                    <p>
                                        <strong>Rotation </strong> - Counts each PLAY for the song.
                                    </p>
                                    <p>
                                        <strong>Library</strong> - Counts each SONG only once.
                                    </p>
                                </RATooltip>
                            </span>
                            <ToggleButtonGroup type={"radio"} name="grouping" value={grouping} onChange={handleGroupingChange} className="ra-switcher">
                                <ToggleButton id={`${moduleName}-rotation`} variant={"ra-dark-blue-outline"} value={false}>
                                    Rotation
                                </ToggleButton>
                                <ToggleButton id={`${moduleName}-library`} className=" text-nowrap" variant={"ra-dark-blue-outline"} value={true}>
                                    Library
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                        <Card>
                            <Card.Header className="text-center">Release Year Chart (%)</Card.Header>
                            <Card.Body className="d-flex flex-column p-1 justify-content-center overflow-auto">
                                {loading ? (
                                    <LoadingIndicator width={75} />
                                ) : error ? (
                                    <ErrorResponse errorCode={error} />
                                ) : chartData ? (
                                    <Col className="dashboard-chart release-year-chart">
                                        <Chart type="bar" data={chartData} options={chartOptions} />
                                    </Col>
                                ) : (
                                    "No Data Available"
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                    <hr className="mt-2" />
                </>
            )}
        </div>
    );
}
